import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FaUsers,
  FaComments,
  FaHandsHelping,
  FaChartBar,
  FaMobile,
  FaGlobeAfrica,
} from "react-icons/fa";

const ServicesSection = styled.section`
  padding: 6rem 0;
  background: white;

  @media (max-width: 768px) {
    padding: 4rem 0;
  }

  @media (max-width: 480px) {
    padding: 3rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: 3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 1rem;

  @media (max-width: 1024px) {
    font-size: 2.5rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.2rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto 4rem auto;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 3rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    margin-bottom: 3rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const ServiceCard = styled(motion.div)`
  background: white;
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 102, 204, 0.15);
    border-color: #0066cc;
  }

  @media (max-width: 768px) {
    padding: 2rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
  }
`;

const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #005d3b 0%, #002b16 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem auto;
  color: white;
  font-size: 2rem;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
`;

const ServiceDescription = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  text-align: left;

  li {
    color: #666;
    margin-bottom: 0.5rem;
    position: relative;
    padding-left: 1.5rem;

    &::before {
      content: "✓";
      position: absolute;
      left: 0;
      color: #005d3b;
      font-weight: bold;
    }
  }
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #005d3b 0%, #002b16 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 2rem auto 0 auto;
  display: block;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 102, 204, 0.3);
  }
`;

const Services = () => {
  const services = [
    {
      icon: <FaUsers />,
      title: "Community Building",
      description:
        "Connect with like-minded citizens and build stronger communities through digital platforms.",
      features: [
        "Community forums and discussions",
        "Local event organization",
        "Member networking",
        "Collaborative project management",
      ],
    },
    {
      icon: <FaComments />,
      title: "Voice & Dialogue",
      description:
        "Turn conversations into action with tools that amplify your voice and facilitate meaningful dialogue.",
      features: [
        "Public opinion sharing",
        "Structured discussions",
        "Consensus building",
        "Action planning",
      ],
    },
    {
      icon: <FaHandsHelping />,
      title: "Accountability Tools",
      description:
        "Hold leaders and institutions accountable with transparent tracking and reporting systems.",
      features: [
        "Progress tracking",
        "Transparency reports",
        "Performance monitoring",
        "Public accountability",
      ],
    },
    {
      icon: <FaChartBar />,
      title: "Impact Analytics",
      description:
        "Measure and understand the impact of community actions with comprehensive analytics.",
      features: [
        "Impact measurement",
        "Progress visualization",
        "Success tracking",
        "Data-driven insights",
      ],
    },
    {
      icon: <FaMobile />,
      title: "Mobile Access",
      description:
        "Stay connected and participate in community activities from anywhere with our mobile platform.",
      features: [
        "Mobile-first design",
        "Offline capabilities",
        "Push notifications",
        "Easy participation",
      ],
    },
    {
      icon: <FaGlobeAfrica />,
      title: "African Focus",
      description:
        "Built specifically for African communities with local context and cultural understanding.",
      features: [
        "Local language support",
        "Cultural sensitivity",
        "African imagery",
        "Local partnerships",
      ],
    },
  ];

  return (
    <ServicesSection id="services">
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          How We Help
        </SectionTitle>

        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          Digital tools and platforms that make community participation simple
          and engaging
        </SectionSubtitle>

        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
            >
              <ServiceIcon>{service.icon}</ServiceIcon>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
              <ServiceFeatures>
                {service.features.map((feature, featureIndex) => (
                  <li key={featureIndex}>{feature}</li>
                ))}
              </ServiceFeatures>
            </ServiceCard>
          ))}
        </ServicesGrid>

        <CTAButton
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Get Started Today
        </CTAButton>
      </Container>
    </ServicesSection>
  );
};

export default Services;
